import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import emailjs from '@emailjs/browser';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGithub,
  FaLinkedin,
  FaFacebook,
  FaClock,
} from 'react-icons/fa';
import SEO from '../components/SEO';

const Contact: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      // Keys come from .env (set in your REACT_APP_EMAILJS_* variables).
      const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';
      const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
      const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';

      if (!PUBLIC_KEY || !SERVICE_ID || !TEMPLATE_ID) {
        throw new Error('EmailJS is not configured');
      }

      // Initialize EmailJS
      emailjs.init(PUBLIC_KEY);
      
      // Send email
      const response = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }
      );

      console.log('✅ Email sent successfully:', response);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch (error) {
      console.error('❌ Email error:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const contactInfo = [
    {
      icon: FaMapMarkerAlt,
      title: 'Location',
      details: 'Pakistan',
      color: '#6C63FF'
    },
    {
      icon: FaPhone,
      title: 'Phone',
      details: '+92 303 3365593',
      color: '#FF6584'
    },
    {
      icon: FaEnvelope,
      title: 'Email',
      details: 'listenrhymes@gmail.com',
      color: '#6C63FF'
    },
    {
      icon: FaClock,
      title: 'Working Hours',
      details: 'Mon-Fri: 9:00 AM - 11:00 PM',
      color: '#FF6584'
    },
  ];

  return (
    <>
      <SEO 
        title="Contact" 
        description="Get in touch with Sarfraz Ahmed - Full Stack Developer. Let's discuss your project." 
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 pt-24">
        <section className="py-12">
          <div className="container-custom px-4">
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-4 py-1 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
                Let's Connect
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-primary mb-4">
                Contact <span className="text-gray-800 dark:text-gray-100">Me</span>
              </h1>
              <p className="text-gray-500 dark:text-gray-400 dark:text-gray-500 max-w-2xl mx-auto">
                Have a project in mind? Let's discuss. How we can work together?
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mt-4" />
            </motion.div>

            <div id="contact" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Info */}
              <div className="lg:col-span-1 space-y-4">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-start space-x-4"
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${info.color}20` }}
                    >
                      <info.icon className="text-xl" style={{ color: info.color }} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 dark:text-gray-500">{info.title}</h4>
                      <p className="text-gray-800 dark:text-gray-100 font-medium">{info.details}</p>
                    </div>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 }}
                  className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg"
                >
                  <h4 className="text-sm font-medium text-gray-400 dark:text-gray-500 mb-4">Follow Me</h4>
                  <div className="flex space-x-3">
                    {[
                      { icon: FaGithub, href: 'https://github.com/sheru-project', color: '#333' },
                      { icon: FaLinkedin, href: 'https://linkedin.com/in/sarfrazahmed1107', color: '#0077B5' },
                      { icon: FaFacebook, href: 'https://www.facebook.com/sherubhai1107', color: '#1DA1F2' },
                    ].map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:shadow-lg transition-all duration-300 hover:scale-110"
                        whileHover={{ y: -5 }}
                        style={{ color: social.color }}
                      >
                        <social.icon className="text-xl" />
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:col-span-2"
              >
                <form id="contact-form" onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 focus:border-primary focus:outline-none transition-colors text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Your Full Name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 focus:border-primary focus:outline-none transition-colors text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Your Email Address"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 focus:border-primary focus:outline-none transition-colors text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="Project Discussion"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 focus:border-primary focus:outline-none transition-colors resize-none text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 caret-primary"
                      placeholder="Tell me about your project..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full px-8 py-3 bg-gradient-to-r from-primary to-gray-800 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {status === 'sending' ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </motion.button>

                  {status === 'success' && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-center animate-fade-in">
                      ✓ Message sent successfully! I'll get back to you soon.
                    </div>
                  )}
                  {status === 'error' && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-center animate-fade-in">
                      ✗ Failed to send message. Please try again or email me directly.
                    </div>
                  )}
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;